
(() => {
  'use strict';

  const reduceMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  const compactQuery = window.matchMedia('(max-width: 749px)');

  function isStandaloneLinkParagraph(node) {
    if (!node || node.tagName !== 'P') return false;
    const text = (node.textContent || '').replace(/\s+/g, ' ').trim();
    const anchors = Array.from(node.querySelectorAll(':scope > a'));

    if (anchors.length !== 1) return false;
    if (node.children.length !== 1) return false;

    const href = anchors[0].getAttribute('href') || '';
    if (!href || href.startsWith('#')) return false;
    if (text.length < 6 || text.length > 90) return false;
    return true;
  }

  function upgradeStandaloneLinks(scope = document) {
    const richAreas = scope.querySelectorAll('.main-page .rte, .article-template__content, .shopify-policy__body');
    richAreas.forEach((area) => {
      area.querySelectorAll('p').forEach((paragraph) => {
        if (!isStandaloneLinkParagraph(paragraph)) return;
        const link = paragraph.querySelector(':scope > a');
        if (!link) return;

        paragraph.classList.add('feengold-link-button-row');
        link.classList.add('button', 'feengold-link-button');

        const href = link.getAttribute('href') || '';
        const external = /^https?:\/\//i.test(href) && !href.includes(location.hostname);
        if (external && !link.hasAttribute('target')) {
          link.setAttribute('target', '_blank');
          link.setAttribute('rel', 'noopener noreferrer');
        }
      });
    });
  }

  function motionAllowed() {
    return !reduceMotionQuery.matches;
  }

  function random(min, max) {
    return Math.random() * (max - min) + min;
  }

  function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }

  function createSpark(x, y, options = {}) {
    if (!motionAllowed()) return null;

    const spark = document.createElement('span');
    spark.className = 'feengold-spark';
    spark.style.left = `${x}px`;
    spark.style.top = `${y}px`;
    spark.style.setProperty('--spark-size', `${options.size || random(compactQuery.matches ? 9 : 10, compactQuery.matches ? 15 : 18)}px`);
    spark.style.setProperty('--spark-duration', `${options.duration || random(950, 1450)}ms`);
    spark.style.setProperty('--spark-drift-x', `${options.driftX || random(-20, 20)}px`);
    spark.style.setProperty('--spark-drift-y', `${options.driftY || random(-34, -8)}px`);
    document.body.appendChild(spark);
    window.setTimeout(() => spark.remove(), 1800);
    return spark;
  }

  function sparkBurstFromRect(rect, config = {}) {
    if (!motionAllowed()) return;

    const count = config.count || (compactQuery.matches ? 6 : 10);
    for (let i = 0; i < count; i += 1) {
      createSpark(
        rect.left + rect.width * random(0.12, 0.88),
        rect.top + rect.height * random(0.18, 0.82),
        {
          size: random(compactQuery.matches ? 8 : 10, compactQuery.matches ? 14 : 18),
          duration: random(980, 1460),
          driftX: random(-18, 18),
          driftY: random(-30, -8)
        }
      );
    }
  }

  function createTrail(startX, startY, endX, endY, config = {}) {
    if (!motionAllowed()) return;

    const count = config.count || (compactQuery.matches ? 6 : 9);
    for (let i = 0; i < count; i += 1) {
      const progress = i / Math.max(count - 1, 1);
      const delay = Math.round(progress * (config.duration || 820));

      window.setTimeout(() => {
        const wobbleX = random(-12, 12);
        const wobbleY = random(-6, 8);
        createSpark(
          startX + (endX - startX) * progress + wobbleX,
          startY + (endY - startY) * progress + wobbleY,
          {
            size: random(9, 16),
            duration: random(900, 1400),
            driftX: random(-16, 16),
            driftY: random(-26, -10)
          }
        );
      }, delay);
    }
  }

  function createShootingStar(startX, startY, endX, endY, options = {}) {
    if (!motionAllowed()) return;

    const star = document.createElement('span');
    star.className = 'feengold-shooting-star';
    const angle = Math.atan2(endY - startY, endX - startX) * (180 / Math.PI);

    star.style.setProperty('--start-x', `${startX}px`);
    star.style.setProperty('--start-y', `${startY}px`);
    star.style.setProperty('--end-x', `${endX}px`);
    star.style.setProperty('--end-y', `${endY}px`);
    star.style.setProperty('--star-angle', `${angle}deg`);
    star.style.setProperty('--star-length', `${options.length || random(18, 28)}rem`);
    star.style.setProperty('--star-duration', `${options.duration || random(1200, 1480)}ms`);

    document.body.appendChild(star);
    window.setTimeout(() => star.remove(), 2100);
  }

  function greetTopTabArea(rect) {
    if (!motionAllowed()) return 0;

    const startX = rect.left + rect.width * random(0.34, 0.66);
    const startY = rect.top + rect.height * random(0.24, 0.76);
    const endX = clamp(startX + random(-130, 130), 72, window.innerWidth - 72);
    const endY = clamp(random(18, 46), 14, 56);
    const effectDuration = compactQuery.matches ? 1200 : 1450;

    createTrail(startX, startY, endX, endY, {
      count: compactQuery.matches ? 6 : 10,
      duration: effectDuration - 240
    });

    createShootingStar(startX, startY, endX, endY, {
      duration: effectDuration,
      length: compactQuery.matches ? 18 : 24
    });

    window.setTimeout(() => {
      createSpark(endX, endY, {
        size: random(16, 22),
        duration: random(1200, 1600),
        driftX: random(-8, 8),
        driftY: random(-10, 8)
      });
      createSpark(endX + random(-12, 12), endY + random(-6, 6), {
        size: random(12, 18),
        duration: random(1100, 1500),
        driftX: random(-10, 10),
        driftY: random(-10, 4)
      });
    }, Math.round(effectDuration * 0.52));

    return effectDuration;
  }

  function isDelayEligibleNavigation(node, event) {
    if (!node || node.tagName !== 'A') return false;
    if (event.defaultPrevented) return false;
    if (event.button && event.button !== 0) return false;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return false;
    if (node.hasAttribute('download')) return false;

    const href = node.getAttribute('href') || '';
    if (!href || href.startsWith('#') || href.startsWith('javascript:')) return false;
    if (href.startsWith('mailto:') || href.startsWith('tel:')) return false;
    if (node.dataset.feengoldDelayNavigation === 'false') return false;

    return true;
  }

  function delayNavigation(node, delay) {
    const href = node.href;
    const target = (node.getAttribute('target') || '').toLowerCase();

    window.setTimeout(() => {
      if (!href) return;

      if (target === '_blank') {
        window.open(href, '_blank');
        return;
      }

      window.location.assign(href);
    }, Math.max(delay || 0, compactQuery.matches ? 1200 : 1450));
  }

  function addMagicToInteractive(scope = document) {
    if (!motionAllowed()) return;

    const selector = [
      '.button',
      'a.button',
      'button.button',
      '.product-form__submit',
      '.shopify-payment-button__button',
      '.header__menu-item',
      '.list-menu__item--link',
      '.announcement-bar a',
      '.footer__social-band .list-social__link',
      '.main-page .rte a.feengold-link-button',
      '.article-template__content a.feengold-link-button'
    ].join(',');

    scope.querySelectorAll(selector).forEach((node) => {
      if (node.dataset.feengoldMagicReady === 'true') return;
      node.dataset.feengoldMagicReady = 'true';

      let lastMove = 0;

      node.addEventListener('mouseenter', () => {
        const rect = node.getBoundingClientRect();
        sparkBurstFromRect(rect, { count: compactQuery.matches ? 6 : 10 });
      });

      node.addEventListener('mousemove', (event) => {
        const now = Date.now();
        if (now - lastMove < (compactQuery.matches ? 130 : 90)) return;

        createSpark(event.clientX, event.clientY, {
          size: random(8, 14),
          duration: random(860, 1280),
          driftX: random(-14, 14),
          driftY: random(-24, -6)
        });

        lastMove = now;
      });

      node.addEventListener('click', (event) => {
        const rect = node.getBoundingClientRect();
        sparkBurstFromRect(rect, { count: compactQuery.matches ? 8 : 14 });
        const effectDuration = greetTopTabArea(rect);

        if (isDelayEligibleNavigation(node, event)) {
          event.preventDefault();
          if (node.dataset.feengoldNavigating === 'true') return;
          node.dataset.feengoldNavigating = 'true';
          delayNavigation(node, effectDuration);
        }
      });
    });
  }

  function addAmbientMagic() {
    if (!motionAllowed()) return;

    const selector = [
      '.header-brand-centered__brand',
      '.footer__brand-lockup',
      '.announcement-bar__announcement',
      '.feengold-conversion-band'
    ].join(',');

    document.querySelectorAll(selector).forEach((node, index) => {
      if (node.dataset.feengoldAmbient === 'true') return;
      node.dataset.feengoldAmbient = 'true';

      const tick = () => {
        if (!document.body.contains(node)) return;
        const rect = node.getBoundingClientRect();
        if (!rect.width || !rect.height) return;

        createSpark(
          rect.left + rect.width * random(0.08, 0.92),
          rect.top + rect.height * random(0.08, 0.92),
          {
            size: random(9, 16),
            duration: random(1000, 1500),
            driftX: random(-16, 16),
            driftY: random(-28, -8)
          }
        );
      };

      tick();
      window.setInterval(tick, 2200 + index * 320);
    });
  }

  function runAll(scope = document) {
    upgradeStandaloneLinks(scope);
    addMagicToInteractive(scope);
  }

  document.addEventListener('DOMContentLoaded', () => runAll());
  window.addEventListener('load', () => {
    runAll();
    addAmbientMagic();
  });

  document.addEventListener('shopify:section:load', (event) => {
    const scope = event.target || document;
    runAll(scope);
    addAmbientMagic();
  });
})();
